import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Box, Typography, List, ListItem, Paper, TextField, Button, IconButton, MenuItem, CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactMarkdown from 'react-markdown';
import { sendChatMessage, createSession, setApiEndpoint } from '../../api/services/agent-studio/AgentFeatures';
import { DEMO_MODELS } from '../../helpers/MockData';
import { useTheme } from '@mui/material/styles';

const ChatTab = ({ model: initialModel, agentId, endpoint, apiKey, availableModels }) => {
  const theme = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [model, setModel] = useState(initialModel);
  const [sessionId, setSessionId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingInterval, setRecordingInterval] = useState(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);

  const models = useMemo(() => {
    if (availableModels && availableModels.length > 0) {
      return availableModels.map(m => ({
        value: m.id || m.value,
        label: m.name || (m.id ? m.id.split('/').pop() : m.value.split('/').pop())
      }));
    }
    return DEMO_MODELS.map(m => ({
      value: m.value || m.id,
      label: m.name || m.label || (m.value ? m.value.split('/').pop() : m.id.split('/').pop())
    }));
  }, [availableModels]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (endpoint) {
      setApiEndpoint(endpoint);
    }
    if (!model && models.length > 0) {
      setModel(models[0].value);
    }
  }, [endpoint, model, models]);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        if (apiKey) {
          const session = await createSession('New Chat', apiKey, endpoint);
          setSessionId(session.sessionId);
        }
      } catch (error) {
        console.error('Failed to create session:', error);
      }
    };
    
    initializeSession();
  }, [apiKey, endpoint]);

  useEffect(() => {
    return () => {
      if (recordingInterval) {
        clearInterval(recordingInterval);
      }
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
    };
  }, [recordingInterval, mediaRecorder]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const audioChunks = [];

      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
        const audioFile = new File([audioBlob], 'recording.wav', { type: 'audio/wav' });
        setAudioFile(audioFile);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingDuration(0);

      const interval = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      setRecordingInterval(interval);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
      if (recordingInterval) {
        clearInterval(recordingInterval);
      }
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlayingAudio(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlayingAudio(false);
    }
  };

  const removeAudioFile = () => {
    setAudioFile(null);
    setIsPlayingAudio(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/flac', 'audio/webm'];
      if (validTypes.includes(file.type) || file.name.match(/\.(mp3|wav|ogg|m4a|flac|webm)$/i)) {
        setAudioFile(file);
      } else {
        alert('Please select a valid audio file (mp3, wav, ogg, m4a, flac, webm)');
      }
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSend = async () => {
    if ((input.trim() === '' && !audioFile) || !model || !sessionId) return;
    
    const userMessage = input.trim() || (audioFile ? `[Audio: ${audioFile.name}]` : '');
    const messageToAdd = {
      text: userMessage,
      sender: 'user',
      hasAudio: !!audioFile,
      audioFile: audioFile ? URL.createObjectURL(audioFile) : null
    };
    
    setMessages(prev => [...prev, messageToAdd]);
    setInput('');
    const currentAudioFile = audioFile;
    setAudioFile(null);
    setIsLoading(true);

    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }
      
      console.log('Sending message with:', {
        sessionId,
        message: userMessage,
        model,
        hasAudio: !!currentAudioFile,
        audioFileName: currentAudioFile?.name,
        apiKey: apiKey ? apiKey.substring(0, 5) + '...' : 'missing',
        endpoint
      });
      
      const response = await sendChatMessage(sessionId, userMessage, model, apiKey, currentAudioFile);
      
      if (!response.body) {
        throw new Error('No response body');
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';
      let isSystemPrompt = model.includes('DeepSeek');
      
      setMessages(prevMessages => [...prevMessages, { text: '', sender: 'ai', isSystemPrompt }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        const chunk = decoder.decode(value);
        buffer += chunk;

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let previousLineWasEmpty = false;

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            let content = line.slice(6);
            accumulatedText += content;
            previousLineWasEmpty = false;
          } else if (line.trim() === '') {
            if (previousLineWasEmpty) {
              accumulatedText += '\n';
              previousLineWasEmpty = false;
            } else {
              previousLineWasEmpty = true;
            }
          }
        }

        setMessages(prevMessages => {
          const newMessages = [...prevMessages];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage?.sender === 'ai') {
            return newMessages.map((msg, i) =>
              i === newMessages.length - 1 
                ? { ...msg, text: accumulatedText } 
                : msg
            );
          }
          return newMessages;
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prevMessages => [
        ...prevMessages,
        {
          text: `Sorry, there was an error processing your request: ${error.message}`,
          sender: 'ai',
        },
      ]);
    } finally {
      setIsLoading(false);
      scrollToBottom();
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 250px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
        <List>
          {messages.map((message, index) => (
            <ListItem
              key={index}
              sx={{
                justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                mb: 1
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  maxWidth: '70%',
                  bgcolor: message.sender === 'user' ? theme.palette.primary.main : 'background.paper',
                  color: message.sender === 'user' ? 'black' : 'white',
                  borderRadius: message.sender === 'user' ? '20px 20px 5px 20px' : '20px 20px 20px 5px'
                }}
              >
                {message.sender === 'user' ? (
                  <Box>
                    <Typography>{message.text}</Typography>
                    {message.hasAudio && message.audioFile && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <audio
                          controls
                          style={{ maxWidth: '200px' }}
                          src={message.audioFile}
                        />
                      </Box>
                    )}
                  </Box>
                ) : (
                  <>
                    {(message.isSystemPrompt || message.text.includes('</think>')) && (
                      <Box sx={{ 
                        position: 'relative',
                        mb: 3,
                        p: 2,
                        pl: 3,
                        borderRadius: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                        borderLeft: '3px solid',
                        borderColor: 'primary.light',
                        '&::before': {
                          content: '"Thinking..."',
                          position: 'absolute',
                          top: -10,
                          left: 12,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: 'primary.light',
                          bgcolor: 'background.paper',
                          px: 1,
                          borderRadius: 1,
                        }
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontStyle: 'italic', 
                            color: 'text.secondary',
                            fontSize: '0.9rem',
                            lineHeight: 1.5,
                          }}
                        >
                          {message.text.includes('</think>') 
                            ? message.text.split('</think>')[0] 
                            : message.text}
                        </Typography>
                      </Box>
                    )}
                    {!message.isSystemPrompt || message.text.includes('</think>') ? (
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <Typography component="div" sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                              {children}
                            </Typography>
                          ),
                          ul: ({ children }) => (
                            <Box 
                              component="ul" 
                              sx={{ 
                                m: 0,
                                pl: 0,
                                listStylePosition: 'inside',
                              }}
                            >
                              {children}
                            </Box>
                          ),
                          ol: ({ children }) => (
                            <Box 
                              component="ol" 
                              sx={{ 
                                m: 0,
                                pl: 0,
                                listStylePosition: 'inside',
                              }}
                            >
                              {children}
                            </Box>
                          ),
                        }}
                      >
                        {message.text.includes('</think>') 
                          ? message.text.split('</think>')[1] 
                          : message.text}
                      </ReactMarkdown>
                    ) : null}
                  </>
                )}
              </Paper>
            </ListItem>
          ))}
          {isLoading && (
            <ListItem sx={{ justifyContent: 'flex-start' }}>
              <Paper sx={{
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: '20px 20px 20px 5px',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.6 }
                }
              }}>
                <CircularProgress 
                  size={24}
                  thickness={4}
                  sx={{ color: theme.palette.primary.main }}
                />
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  fontWeight: 500,
                  background: `linear-gradient(90deg, ${theme.palette.background.paper} 30%, ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'} 50%, ${theme.palette.background.paper} 70%)`,
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  animation: 'shimmer 2s linear infinite',
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' }
                  }
                }}>
                  Generating response...
                </Typography>
              </Paper>
            </ListItem>
          )}
          <div ref={messagesEndRef} />
        </List>
      </Box>

      <Box sx={{ p: 2, maxWidth: '800px', mx: 'auto', width: '100%' }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          bgcolor: 'background.paper',
          borderRadius: '12px', 
          p: 1,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&:focus-within': {
            border: '1px solid',
            borderColor: 'success.main',
          }
        }}>
          <TextField
            select
            size="small"
            value={model || ''}
            onChange={(e) => setModel(e.target.value)}
            sx={{
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                fontSize: '0.875rem',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  }
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  }
                }
              },
              '& .MuiSelect-select': {
                py: 1,
                px: 1.5,
              }
            }}
          >
            {models.map((option) => (
              <MenuItem 
                key={option.value} 
                value={option.value}
                sx={{
                  fontSize: '0.875rem',
                  py: 1,
                  px: 1.5,
                  '&:hover': {
                    bgcolor: 'primary.lighter',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'primary.lighter',
                    '&:hover': {
                      bgcolor: 'primary.light',
                    }
                  }
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {audioFile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {audioFile.name}
              </Typography>
              <audio
                ref={audioRef}
                src={URL.createObjectURL(audioFile)}
                onEnded={() => setIsPlayingAudio(false)}
                style={{ display: 'none' }}
              />
              <IconButton
                size="small"
                onClick={isPlayingAudio ? pauseAudio : playAudio}
              >
                {isPlayingAudio ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
              </IconButton>
              <IconButton
                size="small"
                onClick={removeAudioFile}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            disabled={isLoading || !model}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.default',
                '&:hover fieldset': { borderColor: 'success.main' },
                '&.Mui-focused fieldset': { borderColor: 'success.main' }
              },
              '& .MuiInput-input': {
                py: 1.2,
                px: 2
              },
            }}
          />

          <input
            type="file"
            accept="audio/*"
            onChange={handleFileUpload}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || isRecording}
          >
            <AudiotrackIcon />
          </IconButton>
          
          <IconButton
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isLoading}
            sx={{
              color: isRecording ? 'error.main' : 'inherit',
              animation: isRecording ? 'pulse 1s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.1)' },
                '100%': { transform: 'scale(1)' }
              }
            }}
          >
            {isRecording ? <StopIcon /> : <MicIcon />}
          </IconButton>

          <Button
            onClick={handleSend}
            disabled={isLoading || (!input.trim() && !audioFile) || !model}
            sx={{
              minWidth: 'auto',
              p: 1,
              bgcolor: 'success.main',
              color: 'success.contrastText',
              '&:hover': {
                bgcolor: 'success.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled'
              }
            }}
          >
            <SendIcon />
          </Button>
        </Box>
      </Box>
      
      <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>
        The AI Assistant may make mistakes. Please verify important information.
      </Typography>
    </Box>
  );
};

export default ChatTab; 